import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import {
  ArrowLeft, Trash2, ChevronUp, ChevronDown, GripVertical,
  Type, AlignLeft, Hash, Calendar, CheckSquare, ListFilter, Mail, Phone,
  MapPin, Navigation, User, Users, Image as ImageIcon, Camera, FileText, PenTool,
  Heading, MousePointerClick,
} from 'lucide-react'
import { toast } from 'sonner'
import { useForms, type FormField } from '@/hooks/use-forms'
import { PageHeader, btnPrimary, btnSecondary, useConfirm } from '@/components/rm'
import { UnsavedGuard } from '@/components/unsaved-guard'
import { Input } from '@/components/rm/form'

export const Route = createFileRoute('/_app/forms_/$id_/edit')({
  component: FormBuilder,
})

// Mirrors the Flutter FieldType enum (lib/custom_fields/control_data.dart)
const FIELD_TYPES = [
  { v: 'text', l: 'Text', icon: Type, group: 'Basic' },
  { v: 'paragraph', l: 'Paragraph', icon: AlignLeft, group: 'Basic' },
  { v: 'number', l: 'Number', icon: Hash, group: 'Basic' },
  { v: 'integer', l: 'Whole number', icon: Hash, group: 'Basic' },
  { v: 'checkbox', l: 'Checkbox', icon: CheckSquare, group: 'Basic' },
  { v: 'dropdown', l: 'Dropdown', icon: ListFilter, group: 'Basic' },
  { v: 'date', l: 'Date', icon: Calendar, group: 'Basic' },
  { v: 'email', l: 'Email', icon: Mail, group: 'Contact' },
  { v: 'phone', l: 'Phone', icon: Phone, group: 'Contact' },
  { v: 'contact', l: 'Contact picker', icon: User, group: 'Contact' },
  { v: 'user', l: 'User picker', icon: Users, group: 'Contact' },
  { v: 'address', l: 'Address', icon: MapPin, group: 'Location' },
  { v: 'location', l: 'GPS location', icon: Navigation, group: 'Location' },
  { v: 'image', l: 'Image', icon: ImageIcon, group: 'Media' },
  { v: 'camera', l: 'Camera', icon: Camera, group: 'Media' },
  { v: 'document', l: 'Document', icon: FileText, group: 'Media' },
  { v: 'signature', l: 'Signature', icon: PenTool, group: 'Media' },
  { v: 'header', l: 'Section header', icon: Heading, group: 'Layout' },
  { v: 'button', l: 'Button', icon: MousePointerClick, group: 'Layout' },
] as const

const typeMeta = (v: string) => FIELD_TYPES.find((t) => t.v === v) ?? FIELD_TYPES[0]
const GROUPS = ['Basic', 'Contact', 'Location', 'Media', 'Layout']
const DISPLAY_ONLY = new Set(['header', 'button']) // no data capture / no "required"
const HAS_OPTIONS = new Set(['dropdown'])

function FormBuilder() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { form, fields, isLoadingDetail, updateForm, saveFields, answersOnFields, remove } = useForms(id)
  const [confirmDelete, confirmDeleteDialog] = useConfirm()

  const [loaded, setLoaded] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [rows, setRows] = useState<FormField[]>([])

  // Unsaved-changes tracking (spec 005) — same savedSig idiom as the job form / builders.
  const savedSig = useRef('')
  const sigOf = (v: unknown) => JSON.stringify(v)

  // Seed ONLY once both the form and its fields have loaded (fixes fields not showing).
  if (form && !isLoadingDetail && form.id !== loaded) {
    setLoaded(form.id)
    setName(form.name)
    setRows(fields)
    savedSig.current = sigOf({ name: form.name, rows: fields })
  }

  if (isLoadingDetail && !form) {
    return <div className="min-h-screen bg-canvas-subtle p-10"><div className="h-6 w-40 animate-pulse rounded bg-canvas-subtle" /></div>
  }
  if (!form) {
    return (
      <div className="min-h-screen bg-canvas-subtle p-10">
        <PageHeader title="Form not found" />
        <button className={btnSecondary + ' mt-4'} onClick={() => navigate({ to: '/forms' })}>
          <ArrowLeft className="h-4 w-4" /> Back to forms
        </button>
      </div>
    )
  }

  const setRow = (i: number, p: Partial<FormField>) => setRows(rows.map((r, idx) => (idx === i ? { ...r, ...p } : r)))
  const move = (i: number, d: -1 | 1) => {
    const j = i + d
    if (j < 0 || j >= rows.length) return
    const next = [...rows]
    ;[next[i], next[j]] = [next[j], next[i]]
    setRows(next)
  }
  const addField = (type: string) => {
    const m = typeMeta(type)
    setRows([...rows, { name: m.l, type, required: false, options: type === 'dropdown' ? ['Option 1'] : null, row: rows.length + 1 }])
  }

  const currentSig = sigOf({ name, rows })
  const isDirty = loaded === form.id && currentSig !== savedSig.current

  const save = async () => {
    if (!name.trim()) {
      toast.error('Give the form a name.')
      return
    }
    // A SAVED field with a blanked name must not be silently dropped: dropped means removed,
    // removed means its stored answers cascade away. Only never-saved empties may vanish quietly.
    const blankedSaved = rows.filter((r) => r.id && !r.name.trim())
    if (blankedSaved.length) {
      toast.error('A field has no name. Name it, or remove it with the bin button.')
      return
    }
    const keep = rows.filter((r) => r.name.trim())
    // Anything the form had that the editor no longer has is a deletion, and a deletion takes
    // every stored answer with it. Say exactly what dies before it does. (2026-08-10: one
    // unwarned form save cost ten job descriptions; never again.)
    const keptIds = new Set(keep.map((r) => r.id).filter(Boolean) as string[])
    const removed = fields.filter((f) => f.id && !keptIds.has(f.id))
    if (removed.length) {
      const { answers, attachments } = await answersOnFields(removed.map((f) => f.id!))
      const names = removed.map((f) => `"${f.name}"`).join(', ')
      const one = removed.length === 1
      const parts: string[] = []
      if (answers) parts.push(`${answers.toLocaleString()} saved answer${answers === 1 ? '' : 's'}`)
      if (attachments) parts.push(`${attachments.toLocaleString()} photo${attachments === 1 ? '' : 's'} or file${attachments === 1 ? '' : 's'}`)
      const ok = await confirmDelete({
        title: one ? `Delete the field ${names}?` : `Delete ${removed.length} fields?`,
        body: parts.length
          ? `${names} ${one ? 'holds' : 'hold'} ${parts.join(' and ')} on jobs.${answers ? ' Deleting permanently deletes those answers.' : ''}${attachments ? ' The photos stay on the jobs but stop appearing anywhere.' : ''} This cannot be undone.`
          : `${names} will be removed from the form. Nothing is stored against ${one ? 'it' : 'them'}, so nothing else is lost.`,
        confirmLabel: parts.length ? `Delete and lose ${parts.join(' and ')}` : 'Delete',
        tone: 'danger',
      })
      if (!ok) return
    }
    try {
      await updateForm.mutateAsync({ id: form.id, name: name.trim() })
      await saveFields.mutateAsync({ formId: form.id, fields: keep })
      savedSig.current = currentSig
      toast.success('Form saved')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed')
    }
  }
  const del = async () => {
    if (form.is_primary) { toast.error('The job form cannot be deleted — every job is built on it.'); return }
    if (!confirm('Delete this form?')) return
    // Go pristine first so the delete navigation isn't blocked by the unsaved-changes guard.
    // The awaited mutation lets React flush that render before we navigate.
    setName(form.name)
    setRows(fields)
    savedSig.current = sigOf({ name: form.name, rows: fields })
    await remove.mutateAsync(form.id)
    toast.success('Form deleted')
    navigate({ to: '/forms' })
  }

  return (
    <div className="min-h-screen bg-canvas-subtle">
      <UnsavedGuard when={isDirty} />
      {confirmDeleteDialog}
      <div className="sticky top-0 z-10 border-b border-token-subtle bg-token-surface/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3">
          <button onClick={() => navigate({ to: '/forms/$id', params: { id } })} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-token-secondary hover:text-token-primary">
            <ArrowLeft className="h-4 w-4" /> Form
          </button>
          <div className="flex items-center gap-2">
            {/* The primary job form cannot be deleted: every job hangs off it, and the owner

                asked for the button to be gone after nearly losing it (2026-08-10). */}

            {!form.is_primary && (
              <button className={btnSecondary} onClick={del}>
                <Trash2 className="h-4 w-4" />
              </button>

            )}
            <button className={btnPrimary} onClick={save} disabled={updateForm.isPending || saveFields.isPending}>
              {updateForm.isPending || saveFields.isPending ? 'Saving…' : 'Save form'}
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-8">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Form name"
          className="-ml-1 w-full rounded-md bg-transparent px-1 text-[24px] font-semibold tracking-[-0.02em] text-token-primary outline-none focus:bg-token-surface"
        />

        {/* Field list */}
        <div className="mt-6 space-y-2">
          {rows.length === 0 && (
            <div className="rounded-xl border border-dashed border-token-subtle bg-token-surface px-5 py-10 text-center text-[13px] text-token-secondary">
              No fields yet. Add one from the palette below.
            </div>
          )}
          {rows.map((f, i) => {
            const m = typeMeta(f.type)
            const Icon = m.icon
            return (
              <div key={i} className="rounded-xl border border-token-subtle bg-token-surface p-3 shadow-[0_1px_2px_rgba(16,24,40,0.04)]">
                <div className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 flex-none text-token-muted" />
                  <span className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-canvas-subtle text-token-secondary">
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <input
                    value={f.name}
                    onChange={(e) => setRow(i, { name: e.target.value })}
                    placeholder="Field label"
                    className="min-w-[110px] flex-1 rounded-md bg-transparent px-2 py-1.5 text-[14px] font-medium text-token-primary outline-none focus:bg-token-surface"
                  />
                  <span className="hidden rounded-md bg-token-surface px-2 py-1 text-[11.5px] font-medium text-token-secondary sm:inline-block">{m.l}</span>
                  <button onClick={() => move(i, -1)} disabled={i === 0} className="flex h-7 w-7 items-center justify-center rounded-md text-token-secondary hover:bg-canvas-subtle disabled:opacity-30">
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button onClick={() => move(i, 1)} disabled={i === rows.length - 1} className="flex h-7 w-7 items-center justify-center rounded-md text-token-secondary hover:bg-canvas-subtle disabled:opacity-30">
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="flex h-7 w-7 items-center justify-center rounded-md text-token-muted hover:bg-rose-50 hover:text-rose-500">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                {(!DISPLAY_ONLY.has(f.type) || HAS_OPTIONS.has(f.type)) && (
                  <div className="mt-2 flex items-center gap-4 pl-9">
                    {!DISPLAY_ONLY.has(f.type) && (
                      <label className="inline-flex items-center gap-2 text-[12px] text-token-secondary">
                        <input type="checkbox" checked={f.required} onChange={(e) => setRow(i, { required: e.target.checked })} className="h-3.5 w-3.5" />
                        Required
                      </label>
                    )}
                    {HAS_OPTIONS.has(f.type) && (
                      <Input
                        value={(f.options ?? []).join(', ')}
                        onChange={(e) => setRow(i, { options: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) })}
                        placeholder="Option 1, Option 2, Option 3"
                        className="h-8 flex-1 text-[12px]"
                      />
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Add-field palette */}
        <div className="mt-6 rounded-xl border border-token-subtle bg-token-surface p-4">
          <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-token-secondary">Add a field</p>
          <div className="space-y-3">
            {GROUPS.map((g) => (
              <div key={g}>
                <p className="mb-1.5 text-[11px] font-medium text-token-secondary">{g}</p>
                <div className="flex flex-wrap gap-1.5">
                  {FIELD_TYPES.filter((t) => t.group === g).map((t) => (
                    <button
                      key={t.v}
                      onClick={() => addField(t.v)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-token-subtle bg-token-surface px-2.5 py-1.5 text-[12.5px] font-medium text-token-secondary transition-colors hover:border-token-strong hover:bg-token-surface"
                    >
                      <t.icon className="h-3.5 w-3.5 text-token-secondary" /> {t.l}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
